import { Theme } from "../../appearance/Themes";
import ShipIcon from "../../shared/Icons/ShipIcon";
import { ReactComponent as CaretDown } from "../../shared/assets/caretDown.svg";
import { ReactComponent as CaretUp } from "../../shared/assets/caretUp.svg";
import { ReactComponent as CheckRound } from "../../shared/assets/check-round.svg";
import { themeAtom } from "../../shared/atoms";
import { VoyageItineraryM } from "../models/voyage.interfaces";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

type ItineraryProps = {
  itinerary: VoyageItineraryM[] | undefined;
  expandedIndexes: boolean[];
  toggleExpand: (index: number) => void;
  renderCargoDetails: (item: VoyageItineraryM) => JSX.Element | null;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusBadgeClass = (theme: Theme, portFunc: string) => {
  return (
    theme.voyageItinerary.badge[portFunc as keyof typeof theme.voyageItinerary.badge] ??
    theme.voyageItinerary.badge.default
  );
};

const getMarkerClass = (theme: Theme, item: VoyageItineraryM) => {
  return (
    theme.voyageItinerary.marker[item.VI_PORT_FUNC as keyof typeof theme.voyageItinerary.marker] ??
    theme.voyageItinerary.marker.default
  );
};

function TimelineAnchorIcon({ className = "h-[18px] w-[18px]" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="5" r="2.25" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7.5v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M5 12H2.75a9.25 9.25 0 0 0 18.5 0H19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m7.5 17 4.5 3.5 4.5-3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TimelineMapPinIcon({ className = "h-[18px] w-[18px]" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 21.5c-1.8-1.55-7-6.43-7-11.5a7 7 0 1 1 14 0c0 5.07-5.2 9.95-7 11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function TimelineFuelIcon({ className = "h-[18px] w-[18px]" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 4 0v-7a2 2 0 0 0-.6-1.42L18 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 21V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 21h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M3 9h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const renderMarkerIcon = (item: VoyageItineraryM) => {
  if (item.VI_PORT_FUNC === "C" || item.VI_PORT_FUNC === "L" || item.VI_PORT_FUNC === "Y") {
    return <CheckRound className="h-4 w-4" />;
  }

  if (item.VI_PORT_FUNC === "P") {
    return <TimelineMapPinIcon />;
  }

  if (item.VI_PORT_FUNC === "F") {
    return <TimelineFuelIcon />;
  }

  if (item.VI_PORT_FUNC === "O") {
    return <ShipIcon className="h-[18px] w-[18px]" />;
  }

  return <TimelineAnchorIcon />;
};

export function VoyageItinerary({
  itinerary,
  expandedIndexes,
  toggleExpand,
  renderCargoDetails,
}: Readonly<ItineraryProps>) {
  const theme = useAtomValue(themeAtom);

  return (
    <div
      className={twMerge(
        "flex max-h-[675px] w-full flex-col overflow-hidden rounded-lg border",
        theme.home.tailwind.panelBackground,
        theme.uwhcAssistant.chatUi.panelBorder,
      )}
    >
      <div
        className={twMerge(
          "rounded-t-lg border-b px-6 py-4",
          theme.uwhcAssistant.chatUi.panelBorder,
          theme.opsKnowledgeAssistant.chatUi.headerBg,
        )}
      >
        <div className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>Itinerary</div>
      </div>
      <div
        className={twMerge(
          "min-h-0 overflow-y-auto px-6 py-5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:w-1.5",
          theme.uwhcAssistant.chatUi.mainScrollbar,
          theme.uwhcAssistant.chatUi.mainScrollbarTrack,
        )}
      >
        {!itinerary?.length && (
          <div
            className={twMerge(
              "flex h-auto w-max flex-col items-center justify-center font-[sans-serif]",
              theme.home.tailwind.textDefault,
            )}
          >
            No Data Available
          </div>
        )}
        {Boolean(itinerary?.length) && (
          <div className="relative">
            <div className="space-y-4">
              {itinerary?.map((item, index: number) => {
                const isLoading = item.VI_PORT_FUNC === "L";
                const isDischarging = item.VI_PORT_FUNC === "D";
                const isExpandable = isLoading || isDischarging;
                const isExpanded = expandedIndexes[index];
                const isLastRow = index === itinerary.length - 1;
                const showEta = (isLoading || isDischarging) && item.ETA_LOAD_DISCHARGE && !isExpanded;
                const shouldCenterRowContent =
                  !item.ETA_LOAD_DISCHARGE && ["C", "P", "O", "F"].includes(item.VI_PORT_FUNC);

                return (
                  <div className="relative" key={index}>
                    {!isLastRow && (
                      <div
                        className={twMerge(
                          "absolute bottom-[-1.25rem] left-4 top-8 w-0.5",
                          theme.voyageItinerary.connector,
                        )}
                      />
                    )}
                    <div className={twMerge("flex gap-4", shouldCenterRowContent ? "items-center" : "items-start")}>
                      <div
                        className={twMerge(
                          "relative flex w-8 shrink-0 justify-center",
                          !shouldCenterRowContent && "mt-1",
                        )}
                      >
                        <div
                          className={twMerge(
                            "relative z-10 flex h-8 w-8 items-center justify-center rounded-full",
                            getMarkerClass(theme, item),
                          )}
                        >
                          {renderMarkerIcon(item)}
                        </div>
                      </div>

                      <div className={twMerge("min-w-0 flex-1", shouldCenterRowContent ? "pt-0" : "pt-0.5")}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div
                              className={twMerge(
                                "truncate text-sm font-medium",
                                item.IS_DEPARTED === "Y"
                                  ? theme.voyageItinerary.mutedText
                                  : theme.home.tailwind.textDefault,
                              )}
                            >
                              {item.VI_PORT_NAME}
                            </div>
                            {showEta && (
                              <div className={twMerge("mt-1 text-xs", theme.voyageItinerary.mutedText)}>
                                ETA: {formatDate(item.ETA_LOAD_DISCHARGE)}
                              </div>
                            )}
                          </div>

                          <div className="flex shrink-0 justify-end">
                            {isExpandable ? (
                              <button
                                type="button"
                                className={twMerge(
                                  "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium tracking-wide",
                                  getStatusBadgeClass(theme, item.VI_PORT_FUNC),
                                  item.IS_DEPARTED === "Y" && "opacity-70",
                                )}
                                onClick={() => toggleExpand(index)}
                                aria-expanded={isExpanded}
                              >
                                <span>{portFuncMapping[item.VI_PORT_FUNC] ?? ""}</span>
                                {isExpanded ? (
                                  <CaretUp className="ml-1 h-3 w-3" />
                                ) : (
                                  <CaretDown className="ml-1 h-3 w-3" />
                                )}
                              </button>
                            ) : (
                              <div
                                className={twMerge(
                                  "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium tracking-wide",
                                  getStatusBadgeClass(theme, item.VI_PORT_FUNC),
                                  item.IS_DEPARTED === "Y" && "opacity-70",
                                )}
                              >
                                <span>{portFuncMapping[item.VI_PORT_FUNC] ?? ""}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {isExpanded && (
                          <div
                            className={twMerge(
                              "mt-3 overflow-hidden rounded-lg border",
                              theme.voyageItinerary.detailsBorder,
                              theme.voyageItinerary.detailsBackground,
                            )}
                          >
                            {renderCargoDetails(item)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const portFuncMapping: { [key: string]: string | undefined } = {
  Y: "DELIVERY",
  P: "PASSING",
  I: "CANAL TRANSIT",
  F: "FUELING",
  R: "REPAIR",
  L: "LOADING",
  T: "TERMINATING",
  D: "DISCHARGING",
  C: "COMMENCING",
  O: "OTHER",
  Z: "REDELIVERY",
  W: "WAITING",
};
